import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import { Tooltip } from "@mui/material";
const Buttons = ({ buttons, onButtonClick, tableData, isExpanded = false, isLatestMessage = true, clickedButtonIndex = null, previousMessageWidth = 0, sessionId, showProgress = false, progressPercentage = 0, progressText = "Processing...", }) => {
    const [loadingButtonIndex, setLoadingButtonIndex] = React.useState(null);
    const [buttonProgress, setButtonProgress] = React.useState(0);
    const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);
    // Add CSS animation for spinner and progress bar (injected into head)
    React.useEffect(() => {
        const style = document.createElement("style");
        style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      @keyframes progressSlide {
        0% { transform: translateX(-100%); }
        50% { transform: translateX(0%); }
        100% { transform: translateX(100%); }
      }
    `;
        document.head.appendChild(style);
        return () => {
            document.head.removeChild(style);
        };
    }, []);
    // Progress timer effect
    React.useEffect(() => {
        let progressInterval;
        if (loadingButtonIndex !== null) {
            // Reset progress when starting
            setButtonProgress(0);
            progressInterval = setInterval(() => {
                setButtonProgress((prevProgress) => {
                    const newProgress = prevProgress + 1;
                    if (newProgress >= 100) {
                        // Don't clear loading state here, let the actual operation complete
                        return 100;
                    }
                    return newProgress;
                });
            }, 6000); // 6 seconds = 6000ms
        }
        else {
            // Reset progress when not loading
            setButtonProgress(0);
        }
        return () => {
            if (progressInterval) {
                clearInterval(progressInterval);
            }
        };
    }, [loadingButtonIndex]);
    // Update window width on resize
    React.useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [setWindowWidth]);
    let buttonData = [];
    // Parse button data
    try {
        buttonData = JSON.parse(buttons);
        // Validate that upload_link can only be present if upload is true
        buttonData.forEach((button, index) => {
            if (button.upload_link && button.upload !== "true") {
                console.warn(`Button at index ${index}: upload_link can only be present when upload is true`);
                // Remove upload_link if upload is not true
                delete button.upload_link;
            }
            // Validate that key can only be present if type is "download"
            if (button.key && button.type !== "download") {
                console.warn(`Button at index ${index}: key can only be present when type is "download"`);
                // Remove key if type is not "download"
                delete button.key;
            }
        });
    }
    catch (error) {
        console.error("Error parsing button data:", error);
        return _jsx("div", { children: "Error parsing button data" });
    }
    // Progress Bar component
    const ProgressBar = ({ percentage, text, }) => (_jsxs("div", { style: {
            width: "100%",
            maxWidth: "400px",
            margin: "12px 0",
            padding: "16px",
            backgroundColor: "#f8f9fa",
            borderRadius: "12px",
            border: "1px solid #e9ecef",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }, children: [_jsxs("div", { style: {
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "8px",
                }, children: [_jsx("span", { style: {
                            fontSize: "14px",
                            fontWeight: "500",
                            color: "#495057",
                            fontFamily: '"IBM Plex Sans", System-UI, sans-serif',
                        }, children: text }), _jsxs("span", { style: {
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#0E497A",
                            fontFamily: '"IBM Plex Sans", System-UI, sans-serif',
                        }, children: [Math.round(percentage), "%"] })] }), _jsx("div", { style: {
                    width: "100%",
                    height: "8px",
                    backgroundColor: "#e9ecef",
                    borderRadius: "4px",
                    overflow: "hidden",
                }, children: _jsx("div", { style: {
                        width: `${Math.min(Math.max(percentage, 0), 100)}%`,
                        height: "100%",
                        backgroundColor: "#0E497A",
                        borderRadius: "4px",
                        transition: "width 0.3s ease-in-out",
                        background: "linear-gradient(90deg, #0E497A 0%, #1a5a8a 100%)",
                    } }) })] }));
    // Determine layout based on screen size and expansion state
    const isMobile = windowWidth <= 768;
    const containerStyle = {
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        flexWrap: isMobile ? "nowrap" : "wrap",
        gap: "8px",
        margin: "12px 0",
        marginLeft: isMobile ? "0" : "5%",
        justifyContent: "left",
        alignItems: isMobile ? "stretch" : "flex-start",
        maxWidth: "100%",
        width: isMobile ? "100%" : "fit-content",
        pointerEvents: "auto",
    };
    const isDisabled = !isLatestMessage || loadingButtonIndex !== null;
    const buttonStyle = (color, index, button) => {
        const isCurrentButtonLoading = loadingButtonIndex === index;
        const isAnyButtonLoading = loadingButtonIndex !== null;
        const isCurrentButtonDisabled = isDisabled && !isCurrentButtonLoading;
        return {
            padding: "8px 24px",
            borderRadius: "25px",
            border: button.align === "right"
                ? isCurrentButtonDisabled
                    ? "2px solid #e0e0e0"
                    : "2px solid #0E497A"
                : "none",
            backgroundColor: clickedButtonIndex === index
                ? button.align
                    ? "#0E497A"
                    : "#FFBE00"
                : isCurrentButtonDisabled
                    ? "#e0e0e0"
                    : button.align === "right"
                        ? "transparent"
                        : (() => {
                            switch (button.color?.toLowerCase()) {
                                case "green": return "#1BBC9B"; // Green
                                case "orange": return "#F39C12"; // Orange
                                case "purple": return "#9B59B6"; // Purple
                                case "blue": return "#0E497A"; // Standard Blue
                                default: return "#0E497A"; // Default Blue
                            }
                        })(),
            color: isCurrentButtonDisabled
                ? "#0E497A"
                : button.align === "right"
                    ? clickedButtonIndex === index
                        ? "white"
                        : "#0E497A"
                    : clickedButtonIndex === index
                        ? "#0E497A"
                        : "white",
            cursor: isCurrentButtonDisabled
                ? "not-allowed"
                : isCurrentButtonLoading
                    ? "wait"
                    : "pointer",
            fontSize: "12px",
            fontWeight: "500",
            fontFamily: '"IBM Plex Sans", System-UI, sans-serif',
            transition: "all 0.2s ease",
            width: isMobile ? "100%" : "fit-content",
            maxWidth: isMobile ? "100%" : "fit-content",
            minWidth: isMobile ? "100%" : "auto",
            maxHeight: "45px",
            flex: isMobile ? "0 0 auto" : "none",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            boxShadow: isCurrentButtonDisabled
                ? "none"
                : button.align === "right"
                    ? "none"
                    : "0 2px 4px rgba(14, 73, 122, 0.2)",
            opacity: isAnyButtonLoading && !isCurrentButtonLoading ? 0.5 : 1,
        };
    };
    const handleButtonClick = async (button, event, index) => {
        event.preventDefault();
        console.log("Button clicked:", button.text, "Type:", button.type);
        if (isDisabled && loadingButtonIndex !== index) {
            console.log("Button disabled, early return");
            return;
        }
        if (loadingButtonIndex !== null) {
            console.log("Another button loading, early return");
            return;
        }
        // Set loading state for this button
        setLoadingButtonIndex(index);
        console.log("Set loading state for button index:", index);
        try {
            // Handle download type buttons - simple direct download
            if (button.type === "download" && button.download_link) {
                window.open(button.download_link, '_blank');
                setLoadingButtonIndex(null);
                return;
            }
            const shouldTriggerUpload = button.upload === "true";
            const uploadLink = button.upload_link || undefined;
            const hasSuggestions = button.suggestions === "true";
            const validationOptions = shouldTriggerUpload ? {
                acceptedTypes: button.accepted_types,
                mandatoryHeaders: button.mandatory_headers,
                tabsFormat: button.tabs_format,
                errorMessage: button.error_message
            } : undefined;
            onButtonClick(button.prompt, shouldTriggerUpload, index, uploadLink, hasSuggestions, validationOptions, button.error_message);
        }
        catch (error) {
            console.error("Error in handleButtonClick:", error);
        }
        finally {
            // Clear loading state
            console.log("Clearing loading state");
            setLoadingButtonIndex(null);
        }
    };
    // If showProgress is true, show progress bar instead of buttons
    if (showProgress) {
        return (_jsx("div", { style: {
                ...containerStyle,
                justifyContent: "flex-start",
                width: "100%",
                maxWidth: "100%",
            }, children: _jsx(ProgressBar, { percentage: progressPercentage, text: progressText }) }));
    }
    return (_jsx("div", { style: containerStyle, children: buttonData.map((button, index) => {
            const isCurrentButtonLoading = loadingButtonIndex === index;
            const isCurrentButtonDisabled = isDisabled && !isCurrentButtonLoading;
            return (_jsx(Tooltip, { title: button.tooltip, arrow: true, placement: "bottom", open: undefined, PopperProps: {
                    style: { zIndex: 999999 }
                }, children: _jsx("button", { style: buttonStyle(button.color, index, button), onClick: async (e) => await handleButtonClick(button, e, index), disabled: isCurrentButtonDisabled, onMouseEnter: (e) => {
                        if (!isCurrentButtonDisabled && !isCurrentButtonLoading) {
                            e.currentTarget.style.opacity = "0.9";
                            e.currentTarget.style.transform = "translateY(-1px)";
                            e.currentTarget.style.boxShadow = "0 4px 8px rgba(0,0,0,0.15)";
                        }
                    }, onMouseLeave: (e) => {
                        if (!isCurrentButtonDisabled && !isCurrentButtonLoading) {
                            e.currentTarget.style.opacity = "1";
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow = isCurrentButtonDisabled
                                ? "none"
                                : button.align === "right"
                                    ? "none"
                                    : "0 2px 4px rgba(14, 73, 122, 0.2)";
                        }
                    }, children: isCurrentButtonLoading ? (_jsxs("div", { style: {
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: "4px",
                            minWidth: "80px",
                            padding: "2px 0",
                        }, children: [_jsx("div", { style: {
                                    width: "100%",
                                    height: "3px",
                                    backgroundColor: "rgba(255, 255, 255, 0.3)",
                                    borderRadius: "2px",
                                    overflow: "hidden",
                                }, children: _jsx("div", { style: {
                                        width: `${buttonProgress}%`,
                                        height: "100%",
                                        backgroundColor: "#ffffff",
                                        borderRadius: "2px",
                                        transition: "width 0.5s ease-in-out",
                                    } }) }), _jsxs("span", { style: { fontSize: "10px", fontWeight: "500" }, children: [buttonProgress, "% Loading..."] })] })) : (button.text) }) }, index));
        }) }));
};
export default Buttons;
